import React, { useMemo, useState } from 'react';
import { ArrowRight } from 'lucide-react';

const DEFAULT_WORDS = [
  'gooey', 'sticky', 'crispy', 'spicy', 'one-of-a-kind', 'cold',
  'steamy', 'saucy', 'cheesy', 'soft baked', 'sweet', 'smokey',
  'snuggles', 'snow', 'crisp', 'sunny', 'smiles', 'flurries',
  'warm', 'bundle', 'love', 'looking', 'park', 'first',
  'meeting', 'family', 'holiday', 'best'
];

const BATCH_SIZE = 12;

/**
 * Global reusable "select up to N words" tag-picker widget.
 * Props: title, subtitle, words, maxSelect, onNext(selectedWords: string[])
 */
const WordTagPicker = ({
  title = "Let's make magic.",
  subtitle = 'Select up to 5 words to describe your moment',
  words = DEFAULT_WORDS,
  maxSelect = 5,
  onNext,
  children
} = {}) => {
  const [selected, setSelected] = useState([]);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);

  const visibleWords = useMemo(() => words.slice(0, visibleCount), [words, visibleCount]);
  const canSelectMore = selected.length < maxSelect;

  const toggleWord = (word) => {
    setSelected((prev) => {
      if (prev.includes(word)) return prev.filter((w) => w !== word);
      if (prev.length >= maxSelect) return prev;
      return [...prev, word];
    });
  };

  const handleSurpriseMe = () => {
    const shuffled = [...words].sort(() => Math.random() - 0.5);
    setSelected(shuffled.slice(0, maxSelect));
  };

  return (
    <div className="magic-step magic-word-picker px-4 py-4">
      <h3 className="magic-title">{title}</h3>
      <p className="magic-subtitle">{subtitle}</p>

      {children && <div className="magic-word-picker-top">{children}</div>}

      <div className="magic-tag-grid">
        {visibleWords.map((word) => {
          const isSelected = selected.includes(word);
          return (
            <button
              key={word}
              type="button"
              className={`magic-tag${isSelected ? ' magic-tag--selected' : ''}`}
              onClick={() => toggleWord(word)}
              disabled={!isSelected && !canSelectMore}
            >
              {word}
            </button>
          );
        })}
      </div>

      <div className="magic-tag-actions">
        {visibleCount < words.length && (
          <button type="button" className="magic-link-btn" onClick={() => setVisibleCount((c) => c + BATCH_SIZE)}>
            More words
          </button>
        )}
        <button type="button" className="magic-link-btn" onClick={handleSurpriseMe}>
          Surprise Me
        </button>
      </div>

      <button
        type="button"
        className="magic-fab"
        disabled={selected.length === 0}
        onClick={() => onNext && onNext(selected)}
        aria-label="Next"
      >
        <ArrowRight size={22} />
      </button>
    </div>
  );
};

export default WordTagPicker;
